import { useState, useEffect } from 'react'

export default function AccountForm({
  account,
  providerConfigs,
  onSubmit,
  onCancel,
  loading,
  // Provider-config affordances (all optional). When supplied, the form renders
  // View / Edit / New buttons beside the config dropdown so an operator can
  // manage the config without leaving the Accounts page.
  onViewConfig,
  onEditConfig,
  onCreateConfig,
  // Set by the parent after a config is created from this form so the new
  // config becomes the selected one without resetting the rest of the form.
  pendingConfigId
}) {
  const [formData, setFormData] = useState({
    Name: account?.Name || '',
    SmsProviderConfigId: account?.SmsProviderConfigId || '',
    IsDeleted: false
  })

  useEffect(() => {
    if (pendingConfigId) {
      setFormData(prev => ({ ...prev, SmsProviderConfigId: String(pendingConfigId) }))
    }
  }, [pendingConfigId])

  const selectedConfig = providerConfigs?.find(
    c => String(c.SmsProviderConfigId) === String(formData.SmsProviderConfigId)
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      ...formData,
      SmsProviderConfigId: formData.SmsProviderConfigId ? Number(formData.SmsProviderConfigId) : null
    })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const secondaryButton =
    'px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition'

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Account Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="Name"
          value={formData.Name}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Marketing SMS"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          SMS Provider Configuration
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            name="SmsProviderConfigId"
            value={formData.SmsProviderConfigId}
            onChange={handleChange}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">-- Select Provider Config --</option>
            {providerConfigs?.map((config) => (
              <option key={config.SmsProviderConfigId} value={config.SmsProviderConfigId}>
                {config.Name}
              </option>
            ))}
          </select>
          {(onViewConfig || onEditConfig || onCreateConfig) && (
            <div className="flex gap-2">
              {onViewConfig && (
                <button
                  type="button"
                  onClick={() => onViewConfig(selectedConfig)}
                  disabled={!selectedConfig}
                  className={secondaryButton}
                  title="View the selected configuration"
                >
                  View
                </button>
              )}
              {onEditConfig && (
                <button
                  type="button"
                  onClick={() => onEditConfig(selectedConfig)}
                  disabled={!selectedConfig}
                  className={secondaryButton}
                  title="Edit the selected configuration"
                >
                  Edit
                </button>
              )}
              {onCreateConfig && (
                <button
                  type="button"
                  onClick={onCreateConfig}
                  className={secondaryButton}
                  title="Create a new configuration"
                >
                  New
                </button>
              )}
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {selectedConfig?.JsonConfig?.BaseUrl
            ? `Base URL: ${selectedConfig.JsonConfig.BaseUrl}`
            : 'Infobip or other SMS provider settings'}
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          {loading ? 'Saving...' : account ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  )
}
